import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Text,
  useColorModeValue,
  VStack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

const ForgotPasswordForm = ({ onSubmit }) => {
  // Form state
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Error state
  const [errors, setErrors] = useState({
    email: '',
    form: ''
  });
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Get auth context
  const { sendPasswordResetEmail } = useAuth();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({
      email: '',
      form: ''
    });
    
    // Validate inputs
    let isValid = true;
    
    if (!email) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      isValid = false;
    } else if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      isValid = false;
    }
    
    if (!isValid) return;
    
    try {
      setIsLoading(true);
      
      // Send password reset email using Firebase
      const result = await sendPasswordResetEmail(email);
      
      if (result.success) {
        // Show success message
        setIsSubmitted(true);
        
        // Call the onSubmit prop with form data if provided
        if (onSubmit) {
          await onSubmit({ email, success: true });
        }
      } else {
        // Handle error
        setErrors(prev => ({ 
          ...prev, 
          form: result.error || 'Failed to send password reset email. Please try again.' 
        }));
      }
      
    } catch (error) {
      // Handle error
      setErrors(prev => ({ 
        ...prev, 
        form: error.message || 'An error occurred. Please try again.' 
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={bgColor}
      p={8}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      maxW="400px"
    >
      <VStack spacing={4} align="flex-start" width="100%">
        <Text fontSize="2xl" fontWeight="bold">Reset Password</Text>
        
        {isSubmitted ? (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            Password reset instructions have been sent to your email.
          </Alert>
        ) : (
          <>
            <Text fontSize="sm">
              Enter your email address and we'll send you instructions to reset your password.
            </Text>
            
            {errors.form && (
              <Box 
                p={3} 
                bg="red.100" 
                color="red.800" 
                width="100%" 
                borderRadius="md"
              >
                {errors.form}
              </Box>
            )}
            
            <FormControl isInvalid={!!errors.email}>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            
            <Button
              type="submit"
              colorScheme="teal"
              width="100%"
              mt={4}
              isLoading={isLoading}
              loadingText="Submitting"
            >
              Send Reset Link
            </Button>
            
            <Box textAlign="center" width="100%" mt={4}>
              <Button 
                variant="link" 
                colorScheme="blue" 
                size="sm" 
                as="a" 
                href="/login"
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default ForgotPasswordForm;
